import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  InputAdornment,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import InfoIcon from '@mui/icons-material/Info'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import {
  FormProvider,
  RHFSwitch,
  RHFTextField,
} from '../../../common/hook-form'
import {
  apiConstants,
  getGeneric,
  putGeneric,
} from '../../../common/constants'
import PageTitle from '../../../components/PageTitle'
import Loader from '../../../common/Loader'

// rvp: true → claim type supports an RVP-specific window (tatHoursRVPByClaimType)
const CLAIM_TYPES = [
  { key: 'DNR', label: 'Delivered Not Received', desc: 'Forward · RTO · RVP', color: 'warning', rvp: true },
  { key: 'DAMAGED', label: 'Damaged Parcel', desc: 'Forward · RTO · RVP', color: 'error', rvp: true },
  { key: 'WRONG_ITEM', label: 'Wrong Item Received', desc: 'Forward · RTO · RVP', color: 'info', rvp: true },
  { key: 'PARTIAL', label: 'Partial Delivery', desc: 'Forward · RTO · RVP', color: 'primary', rvp: true },
  { key: 'EMPTY', label: 'Empty Parcel', desc: 'Forward · RTO · RVP', color: 'secondary', rvp: true },
  { key: 'NEVER_PICKED', label: 'Never Picked', desc: 'Forward · RVP', color: 'warning', rvp: true },
  { key: 'HANDOVER_DISPUTE', label: 'Handover Dispute', desc: 'Pickup · Cancelled', color: 'warning' },
  { key: 'LOST', label: 'Lost Shipment', desc: '30 days from pickup', color: 'error', readOnly: true, defaultDays: 30 },
]

const DEFAULT_TAT_DAYS = 2
// Backend stores hours — we display/input as days and convert
const hoursToDays = (h) => Math.round((h || 0) / 24) || DEFAULT_TAT_DAYS
const daysToHours = (d) => (d || DEFAULT_TAT_DAYS) * 24

const buildDefaults = () =>
  CLAIM_TYPES.reduce(
    (acc, t) => {
      acc[`tat_${t.key}`] = t.defaultDays ?? DEFAULT_TAT_DAYS
      if (t.rvp) acc[`tatRvp_${t.key}`] = t.defaultDays ?? DEFAULT_TAT_DAYS
      return acc
    },
    { claimsEnabled: true, exceptionalNote: '' }
  )

function ClaimsSettings({ id, data }) {
  const { enqueueSnackbar } = useSnackbar()
  const methods = useForm({ defaultValues: buildDefaults() })
  const {
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = methods

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [reload, setReload] = useState(0)

  const claimsEnabled = watch('claimsEnabled')

  useEffect(() => {
    if (!id) return
    setLoading(true)
    getGeneric(`${apiConstants.CLAIMS_CONFIG}/${id}`)
      .then((res) => {
        const cfg = res?.data?.responseObj || res?.data || {}
        const tatMap = cfg.tatHoursByClaimType || {}
        const rvpTatMap = cfg.tatHoursRVPByClaimType || {}
        const next = buildDefaults()
        CLAIM_TYPES.forEach((t) => {
          const hoursFromApi = tatMap[t.key]
          const forwardDays = hoursFromApi ? hoursToDays(hoursFromApi) : (t.defaultDays ?? DEFAULT_TAT_DAYS)
          next[`tat_${t.key}`] = forwardDays
          if (t.rvp) {
            // No RVP override saved yet → default to the forward window
            const rvpHoursFromApi = rvpTatMap[t.key]
            next[`tatRvp_${t.key}`] = rvpHoursFromApi ? hoursToDays(rvpHoursFromApi) : forwardDays
          }
        })
        next.claimsEnabled = cfg.claimsEnabled ?? true
        next.exceptionalNote = cfg.exceptionalNote || ''
        reset(next)
      })
      .catch((err) => {
        if (err?.response?.status !== 404) {
          enqueueSnackbar('Failed to load claims configuration', { variant: 'error' })
        }
      })
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, reload])

  const onSubmit = (values) => {
    // Validate editable types only (LOST is read-only / managed by backend)
    for (const t of CLAIM_TYPES) {
      if (t.readOnly) continue
      const v = Number(values[`tat_${t.key}`])
      if (!v || v < 1) {
        enqueueSnackbar(`Claim window for ${t.label} must be at least 1 day`, { variant: 'error' })
        return
      }
      if (t.rvp) {
        const rv = Number(values[`tatRvp_${t.key}`])
        if (!rv || rv < 1) {
          enqueueSnackbar(`RVP claim window for ${t.label} must be at least 1 day`, { variant: 'error' })
          return
        }
      }
    }
    // Convert days → hours for backend (backend stores hours)
    const tatHoursByClaimType = {}
    const tatHoursRVPByClaimType = {}
    for (const t of CLAIM_TYPES) {
      const days = Number(values[`tat_${t.key}`])
      tatHoursByClaimType[t.key] = days > 0 ? daysToHours(days) : daysToHours(t.defaultDays ?? DEFAULT_TAT_DAYS)
      if (t.rvp) {
        const rvpDays = Number(values[`tatRvp_${t.key}`])
        tatHoursRVPByClaimType[t.key] = rvpDays > 0 ? daysToHours(rvpDays) : tatHoursByClaimType[t.key]
      }
    }
    const payload = {
      tatHoursByClaimType,
      tatHoursRVPByClaimType,
      claimsEnabled: !!values.claimsEnabled,
      exceptionalNote: values.exceptionalNote || '',
    }
    setSaving(true)
    putGeneric(`${apiConstants.CLAIMS_CONFIG}/${id}`, payload)
      .then(() => {
        enqueueSnackbar('Configuration saved successfully', { variant: 'success' })
        setReload((r) => r + 1)
      })
      .catch((err) => {
        enqueueSnackbar(
          err?.response?.data?.message || 'Failed to save configuration',
          { variant: 'error' }
        )
      })
      .finally(() => setSaving(false))
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pb: isDirty ? '90px' : { xs: 2, md: 3 }, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {(loading || saving) && <Loader />}
      <PageTitle>Claims Configuration {data?.name ? ' / ' + data.name : ''}</PageTitle>

      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        {/* ─── Module Access ─────────────────────────── */}
        <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Module Access
              </Typography>
              <Tooltip title="Controls visibility of the Raise Claim button on the merchant panel.">
                <InfoIcon color="action" fontSize="small" />
              </Tooltip>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={2}>
              <Box flex={1}>
                <Typography variant="body1" fontWeight={500}>
                  Enable claims for this merchant
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {claimsEnabled
                    ? 'Merchant can raise claims on eligible Forward / RTO / RVP shipments.'
                    : "Raise Claim button is hidden across all of this merchant's shipments."}
                </Typography>
              </Box>
              <RHFSwitch name="claimsEnabled" label="" />
            </Stack>
          </CardContent>
        </Card>

        {/* ─── TAT Configuration ─────────────────────────── */}
        <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Claim Window — Days per Claim Type
              </Typography>
              <Tooltip title="Time window (in days) within which a merchant can raise a claim, measured from the relevant delivery / pickup event.">
                <InfoIcon color="action" fontSize="small" />
              </Tooltip>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Default is {DEFAULT_TAT_DAYS} days. Changes apply to new claims only — existing
              claims keep the TAT they were filed under.
            </Typography>

            <Grid container spacing={2}>
              {CLAIM_TYPES.map((t) => (
                <Grid item xs={12} sm={6} md={4} key={t.key}>
                  <Box
                    sx={{
                      p: 2,
                      height: '100%',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                      <Box flex={1} sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {t.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t.desc}
                        </Typography>
                      </Box>
                      <Chip
                        label={t.key}
                        size="small"
                        color={t.color}
                        variant="outlined"
                        sx={{ fontWeight: 500, fontSize: 10 }}
                      />
                    </Stack>
                    <RHFTextField
                      name={`tat_${t.key}`}
                      label={t.rvp ? 'Forward / RTO Window (days)' : 'Claim Window (days)'}
                      type="number"
                      size="small"
                      disabled={t.readOnly}
                      helperText={t.readOnly ? 'Managed centrally — not editable' : undefined}
                      InputProps={{
                        readOnly: t.readOnly,
                        endAdornment: <InputAdornment position="end">days</InputAdornment>,
                      }}
                    />
                    {t.rvp && (
                      <RHFTextField
                        name={`tatRvp_${t.key}`}
                        label="RVP Window (days)"
                        type="number"
                        size="small"
                        sx={{ mt: 1.5 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">days</InputAdornment>,
                        }}
                      />
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* ─── Internal Note ─────────────────────────── */}
        <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Internal Note
              </Typography>
              <Tooltip title="Optional admin-only note. Not shown to the merchant.">
                <InfoIcon color="action" fontSize="small" />
              </Tooltip>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Optional. Visible to admins only.
            </Typography>
            <RHFTextField
              name="exceptionalNote"
              label="Exceptional TAT Note (admin-only)"
              placeholder="e.g. Extended TAT agreed in MSA section 4.2"
              multiline
              minRows={3}
              fullWidth
            />
          </CardContent>
        </Card>

        {/* ─── Action Footer (fixed to viewport bottom when unsaved) ──── */}
        <Box
          sx={{
            position: isDirty ? 'fixed' : 'relative',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: isDirty ? 1300 : 'auto',
            transition: 'box-shadow 0.3s ease, background-color 0.3s ease',
          }}
        >
          <Card
            sx={{
              border: '1px solid',
              borderColor: isDirty ? 'warning.main' : 'divider',
              backgroundColor: isDirty ? 'warning.lighter' : 'background.paper',
              boxShadow: isDirty ? '0 -4px 12px rgba(0,0,0,0.15)' : 'none',
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Badge
                    variant="dot"
                    color={isDirty ? 'warning' : 'success'}
                    sx={{ '& .MuiBadge-dot': { width: 10, height: 10, borderRadius: '50%' } }}
                  >
                    <Box sx={{ pl: 1 }} />
                  </Badge>
                  <Typography
                    variant="body2"
                    color={isDirty ? 'warning.dark' : 'text.secondary'}
                    fontWeight={isDirty ? 600 : 400}
                  >
                    {isDirty ? 'You have unsaved changes' : 'All changes saved'}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1.5}>
                  <Button
                    type="button"
                    variant="outlined"
                    color="inherit"
                    startIcon={<RestartAltIcon />}
                    onClick={() => setReload((r) => r + 1)}
                    disabled={saving || !isDirty}
                  >
                    Reset
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color={isDirty ? 'warning' : 'primary'}
                    startIcon={<SaveOutlinedIcon />}
                    disabled={saving || !isDirty}
                    sx={{ minWidth: 180 }}
                  >
                    Save Configuration
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </FormProvider>
    </Box>
  )
}

export default ClaimsSettings
